/**
 * § 362 — what the Tab key actually reaches, in ONE place.
 *
 * The modal Tab cycle (`useDismissable`) and Popover's Tab-out both hinge on a surface's
 * FIRST and LAST tab stop, and each computed them with its own "focusable" selector. Being
 * focusable is not the same as being a Tab stop, and the difference let focus fall out of a
 * modal or a popover:
 *
 * - **`tabindex="-1"` controls were counted as stops.** Both selectors listed `button`, so a
 *   NumberField's steppers, a Combobox's toggle, a SegmentedControl's unchecked segments, a
 *   Tab list's inactive tabs, a FileUpload's input and a Table's roving rows could be the
 *   "last item". Tab from the REAL last stop (a NumberField's input, say) then went
 *   unrecognised as the edge, and the browser moved focus past the surface.
 * - **Hidden subtrees were counted.** Only the element's OWN `hidden` / `aria-hidden` was
 *   checked, so a button inside a `hidden` container, a disabled `<fieldset>` or a closed
 *   `<details>` could be the edge, and the browser can never focus it.
 * - **A radio group was N stops.** Tab visits one radio per group (the checked one, or,
 *   when none is, the first one Tab meets in that direction), not all of them.
 * - **Layout was ignored.** In a browser, `display: none` and `visibility: hidden` controls
 *   are not reachable. jsdom has no layout, so that check runs only where layout exists
 *   (`clientWidth` is 0 under jsdom and never in a browser — the scroll lock's test).
 *
 * Deliberately attribute-first, so the same rules hold under test.
 */

const CANDIDATE = [
  'a[href]',
  'area[href]',
  'button',
  'input',
  'select',
  'textarea',
  'iframe',
  'summary',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]',
].join(', ');

export type TabDirection = 'forward' | 'backward';

/** A real layout engine is present. jsdom reports `clientWidth` 0; a browser never does. */
const hasLayout = (doc: Document): boolean => doc.documentElement.clientWidth > 0;

const isRendered = (el: HTMLElement): boolean => {
  if (typeof el.checkVisibility === 'function') {
    return el.checkVisibility({ checkVisibilityCSS: true, visibilityProperty: true });
  }
  if (el.getClientRects().length === 0) return false;
  return getComputedStyle(el).visibility === 'visible';
};

/** Inside `root` (the element itself included), under something the browser will not tab into. */
const isShut = (el: HTMLElement, root: HTMLElement): boolean => {
  const within = (node: Element | null) => node !== null && (node === root || root.contains(node));
  if (within(el.closest('[hidden], [inert], [aria-hidden="true"]'))) return true;
  // A closed <details> renders nothing but its own summary.
  const details = el.closest('details');
  if (within(details) && !(details as HTMLDetailsElement).open) {
    const summary = el.closest('summary');
    return !(summary && summary.parentElement === details);
  }
  return false;
};

const isStop = (el: HTMLElement, root: HTMLElement, layout: boolean): boolean => {
  const explicit = el.getAttribute('tabindex');
  if (explicit !== null && Number.parseInt(explicit, 10) < 0) return false;
  if (el instanceof HTMLInputElement && el.type === 'hidden') return false;
  if (el.matches(':disabled')) return false;
  if (el.tagName === 'SUMMARY' && el.parentElement?.tagName !== 'DETAILS') return false;
  if (isShut(el, root)) return false;
  if (layout && !isRendered(el)) return false;
  return true;
};

const isNamedRadio = (el: HTMLElement): el is HTMLInputElement =>
  el instanceof HTMLInputElement && el.type === 'radio' && el.name !== '';

/**
 * The Tab stops inside `root`, in DOM order. A named radio group contributes ONE stop: its
 * checked radio, or, with none checked, the first radio Tab meets travelling in `direction`.
 */
export function tabbablesIn(root: HTMLElement, direction: TabDirection = 'forward'): HTMLElement[] {
  const layout = hasLayout(root.ownerDocument);
  const stops = [...root.querySelectorAll<HTMLElement>(CANDIDATE)].filter((el) => isStop(el, root, layout));

  const groups = new Map<HTMLFormElement | null, Map<string, HTMLInputElement[]>>();
  for (const el of stops) {
    if (!isNamedRadio(el)) continue;
    const byName = groups.get(el.form) ?? new Map<string, HTMLInputElement[]>();
    groups.set(el.form, byName);
    byName.set(el.name, [...(byName.get(el.name) ?? []), el]);
  }

  return stops.filter((el) => {
    if (!isNamedRadio(el)) return true;
    const group = groups.get(el.form)?.get(el.name) ?? [el];
    const stop = group.find((radio) => radio.checked) ?? (direction === 'forward' ? group[0] : group[group.length - 1]);
    return el === stop;
  });
}

/**
 * Whether Tab (or Shift+Tab, going `backward`) from `active` would leave the surface whose
 * `stops` these are: no stop lies beyond it in that direction. Positional rather than "is it
 * the last item", so a focused element that is not itself a stop — a stepper clicked with the
 * mouse, the surface itself — is still judged by where it sits. Pass the stops computed for
 * the same `direction`.
 */
export function isAtTabEdge(stops: HTMLElement[], active: Element, direction: TabDirection): boolean {
  if (stops.length === 0) return true;
  const beyond = direction === 'forward' ? Node.DOCUMENT_POSITION_FOLLOWING : Node.DOCUMENT_POSITION_PRECEDING;
  return !stops.some((stop) => stop !== active && (active.compareDocumentPosition(stop) & beyond) !== 0);
}
